/// <amd-module name="ServiceHooks/Web/Admin/Utilities" />
import { Notification, NotificationResult, NotificationStatus } from "ServiceHooks/Web/Admin/Contracts/ServiceHooks";
import * as Resources from "ServiceHooks/Web/Admin/Resources";
import { FormatComponent } from "VSS/Platform/Components/Format/Format";
import { css } from "VSS/Platform/Layout";
import { Link } from "VSSUI/Link";
import { IStatusProps, Statuses } from "VSSUI/Status";
import { ITableColumn, SimpleTableCell, TableColumnStyle } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import * as React from "react";
import { Consumer, ConsumerAction, EventTypeDescriptor, Subscription, SubscriptionStatus } from "./Contracts/ServiceHooks";

export function getNotificationStatusBundle(notification: Notification): { statusProps: IStatusProps; label: string } {
    if (notification.status === NotificationStatus.Completed) {
        switch (notification.result) {
            case NotificationResult.Succeeded:
                return { statusProps: Statuses.Success, label: Resources.StatusSucceeded };
            default:
                return { statusProps: Statuses.Failed, label: Resources.StatusFailed };
        }
    } else {
        return { statusProps: Statuses.Running, label: Resources.StatusProcessing };
    }
}

export function getStatusForNotificationResult(notificationResult: NotificationResult): IStatusProps {
    switch (notificationResult) {
        case NotificationResult.Succeeded:
            return Statuses.Success;
        case NotificationResult.Failed:
            return Statuses.Failed;
        default:
            return Statuses.Running;
    }
}

export function getTextForNotificationResult(notificationResult: NotificationResult): string {
    switch (notificationResult) {
        case NotificationResult.Succeeded:
            return Resources.StatusSucceeded;
        case NotificationResult.Failed:
            return Resources.StatusFailed;
        default:
            return Resources.StatusProcessing;
    }
}

export function getTextCellRenderer<T>(mappingFunction: (item: T) => string, columnStyle?: TableColumnStyle) {
    return (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<T>, tableItem: T) => (
        <SimpleTableCell
            className={css(
                columnStyle === TableColumnStyle.Primary && "bolt-table-cell-primary",
                columnStyle === TableColumnStyle.Secondary && "bolt-table-cell-secondary",
                columnStyle === TableColumnStyle.Tertiary && "bolt-table-cell-tertiary"
            )}
            columnIndex={columnIndex}
            key={columnIndex}
            tableColumn={tableColumn}
        >
            <Tooltip overflowOnly={true}>
                <div className="text-ellipsis">{mappingFunction(tableItem)}</div>
            </Tooltip>
        </SimpleTableCell>
    );
}

export function getSubscriptionStatusAsString(status: SubscriptionStatus): string {
    switch (status) {
        case SubscriptionStatus.Enabled:
            return Resources.SubscriptionStatusEnabled;
        case SubscriptionStatus.OnProbation:
            return Resources.SubscriptionStatusOnProbation;
        case SubscriptionStatus.DisabledByUser:
            return Resources.SubscriptionStatusDisabledByUser;
        case SubscriptionStatus.DisabledBySystem:
            return Resources.SubscriptionStatusDisabledBySystem;
        case SubscriptionStatus.DisabledByInactiveIdentity:
            return Resources.SubscriptionStatusDisabledByInactiveIdentity;
        default:
            return Resources.Unknown;
    }
}

export function getConsumerForSubscriptionAsString(consumersById: { [key: string]: Consumer }, subscription: Subscription) {
    return consumersById[subscription.consumerId] ? consumersById[subscription.consumerId].name : Resources.Unknown;
}

export function getConsumerActionForSubscriptionAsString(consumerActionsById: { [key: string]: ConsumerAction }, subscription: Subscription) {
    return consumerActionsById[subscription.consumerId + subscription.consumerActionId]
        ? consumerActionsById[subscription.consumerId + subscription.consumerActionId].name
        : Resources.Unknown;
}

export function getEventTypeForSubscriptionAsString(eventTypesById: { [key: string]: EventTypeDescriptor }, subscription: Subscription) {
    return eventTypesById[subscription.eventType] ? eventTypesById[subscription.eventType].name : Resources.Unknown;
}

const aTagRegex: RegExp = /<a[^>]*>([^<]+)<\/a>/g;
const hrefRegex: RegExp = /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1/;
const contentsRegex: RegExp = /<a[^>]*>([^<]+)<\/a>/;
export function sanitizeLinks(testText: string): JSX.Element {
    let formatString = testText;
    const replacementElements: JSX.Element[] = [];

    let aTagResult = formatString.match(aTagRegex);
    while (aTagResult && aTagResult[0]) {
        formatString = formatString.replace(aTagResult[0], `{${replacementElements.length}}`);

        // Parse out the props we care about
        const hrefResult = aTagResult[0].match(hrefRegex);
        const contentsResult = aTagResult[0].match(contentsRegex);

        if (hrefResult && contentsResult) {
            const formattedHref = hrefResult[2].replace("&amp;", "&");

            // Create the replacement tag
            replacementElements.push(
                <Link 
                    href={formattedHref} 
                    key={replacementElements.length}
                    underline={true} 
                    target="_blank"
                >
                    {contentsResult[1]}
                </Link>
            );
        } else {
            // Pass raw text if we can't find all of the requisite elements (href and contents)
            replacementElements.push(<React.Fragment key={replacementElements.length}>{aTagResult[0]}</React.Fragment>);
        }
        aTagResult = formatString.match(aTagRegex);
    }

    return <FormatComponent format={formatString}>{...replacementElements}</FormatComponent>;
}
